/* eslint-disable no-unused-vars */
import { axios } from '@/services/axios';
import { isEqual } from 'lodash';

/**
 * Checks if a billing note has been modified by comparing with initial notes
 * @param {Object} currentNote - The current billing note
 * @param {Array} initialBillingNotes - The initial billing notes array
 * @returns {boolean} - True if the note has been modified
 */
const isNoteModified = (currentNote, initialBillingNotes) => {
    if (!currentNote.id || !initialBillingNotes?.length) {
        return false;
    }

    const originalNote = initialBillingNotes.find(
        (note) => note.id === currentNote.id,
    );

    if (!originalNote) {
        return false;
    }

    const { tableData: _t1, ...currentNoteWithoutTableData } = currentNote;
    const { tableData: _t2, ...originalNoteWithoutTableData } = originalNote;

    return !isEqual(currentNoteWithoutTableData, originalNoteWithoutTableData);
};

/**
 * Handles billing notes CRUD operations
 * @param {Array} currentBillingNotes - Current billing notes from formData
 * @param {Array} initialBillingNotes - Initial billing notes from initialFormData
 * @param {Array} deletedBillingNoteIDs - Array of deleted billing note IDs
 * @returns {Promise<void>}
 */
export const handleBillingNotesCRUD = async (
    currentBillingNotes,
    initialBillingNotes,
    deletedBillingNoteIDs,
) => {
    const promises = [];

    if (currentBillingNotes?.length) {
        currentBillingNotes.forEach((note) => {
            if (note.id) {
                // Only PUT if the note has been modified
                if (isNoteModified(note, initialBillingNotes)) {
                    promises.push(axios.put('/billingnote', { ...note }));
                }
            } else {
                // POST new notes without an ID
                promises.push(axios.post('/billingnote', { ...note }));
            }
        });
    }

    if (deletedBillingNoteIDs?.length) {
        deletedBillingNoteIDs.forEach((noteId) => {
            if (noteId) {
                promises.push(axios.delete(`/billingnote/${noteId}`));
            }
        });
    }

    if (promises.length > 0) {
        await Promise.all(promises);
    }
};

/**
 * Gets billing notes from formData based on organisation mode
 * @param {Object} formData - The form data object
 * @param {string} organisationMode - 'Company', 'Wholesaler', or 'Partner'
 * @returns {Array} - The billing notes array for the specified mode
 */
export const getBillingNotesByMode = (formData, organisationMode) => {
    switch (organisationMode) {
        case 'Wholesaler':
            return formData?.wholesalerBillingSettings?.billingNotes || [];
        case 'Partner':
            return formData?.partnerBillingSettings?.billingNotes || [];
        case 'Company':
        default:
            return formData?.companyBillingSettings?.billingNotes || [];
    }
};
